import { Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { useTranslations } from 'gt-react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

const ease = [0.22, 1, 0.36, 1] as const

/**
 * Final CTA — dark section matching the hero. Minimal copy, one
 * action. The background mirrors the hero's dark + dot grid
 * so the page bookends with the same visual language.
 */
export function CtaBand() {
  const t = useTranslations('cta_band')

  return (
    <section className="relative overflow-hidden bg-[#0a0a0f] py-24">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.1]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />
      <div
        className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[400px] w-[600px] rounded-full opacity-[0.1] blur-[100px]"
        style={{ background: 'var(--primary)' }}
      />

      <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6">
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, ease }}
          className="text-3xl font-bold tracking-tight text-white sm:text-4xl"
        >
          {t('heading')}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, delay: 0.1, ease }}
          className="mx-auto mt-4 max-w-lg text-base text-white/40"
        >
          {t('subheading')}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, delay: 0.2, ease }}
          className="mt-8"
        >
          <Link
            to="/sign-up"
            className={cn(
              'group inline-flex items-center gap-2 rounded-lg px-8 py-3.5 text-sm font-semibold',
              'bg-white text-[#0a0a0f]',
              'transition-all duration-200',
              'hover:bg-white/90 hover:shadow-[0_0_32px_rgba(88,101,242,0.25)]',
            )}
          >
            {t('cta')}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </motion.div>
      </div>
    </section>
  )
}
